import { PILLARS, INTENT_OPTIONS } from './pillars';
import { PILLAR_TRANSLATIONS, INTENT_DESCRIPTORS } from './translations';

// ============================================
// HELPERS
// ============================================
const getIntentLabel = (intentId) => {
  const option = INTENT_OPTIONS.find(o => o.value === intentId);
  return option?.label || intentId;
};

const getIntentDescriptor = (intentId, type) => {
  return INTENT_DESCRIPTORS[intentId]?.[type] || '';
};

const getPillarName = (pillarId) => {
  return PILLARS.find(p => p.id === pillarId)?.shortName || pillarId;
};

const CONFIDENCE_LABELS = {
  'yes': 'High — You have experienced this and know what works for you',
  'somewhat': 'Emerging — You have felt parts of this, but it is not fully formed',
  'not-really': 'Exploratory — This is more of a curiosity than a known experience',
  'not-sure': 'Unclear — This part of your experience is still undefined'
};

const getConfidenceLabel = (confidence) => {
  return CONFIDENCE_LABELS[confidence] || '';
};

const getConfidenceShort = (confidence) => {
  return { 'yes': 'High', 'somewhat': 'Emerging', 'not-really': 'Exploratory', 'not-sure': 'Unclear' }[confidence] || '';
};

// ============================================
// 1. INTENT ANCHOR
// ============================================
export const generateIntentAnchor = (profile, scores) => {
  const intentLabels = profile.intent.map(getIntentLabel);
  const intentStr = intentLabels.join(' and ').toLowerCase();
  const topDriverNames = scores.topDrivers.slice(0, 2).map(d => getPillarName(d.pillarId).toLowerCase());

  let text = `This profile is about a ${intentStr} experience in the context of "${profile.label}."`;
  if (topDriverNames.length > 0) {
    text += ` This kind of experience tends to work for you when it includes ${topDriverNames.join(' and ')}.`;
  }

  return text;
};

// ============================================
// 2. WHAT HITS HARDEST
// ============================================
export const generateWhatHitsHardest = (profile, scores) => {
  const intentLabels = profile.intent.map(getIntentLabel);
  const intentStr = intentLabels.join(' and ').toLowerCase();
  const topDrivers = scores.topDrivers.slice(0, 3);

  const driverDetails = topDrivers.map(driver => {
    const translation = PILLAR_TRANSLATIONS[driver.pillarId];
    const name = getPillarName(driver.pillarId);

    return {
      pillarId: driver.pillarId,
      name,
      importance: driver.importance,
      whenPresent: translation?.whenPresent?.[0] || `${name} feels present and real`,
      whenNot: translation?.whenMissing?.[0] || `It tends to feel flat without ${name.toLowerCase()}`
    };
  });

  return {
    description: `These are the elements that matter most for a ${intentStr} experience to feel right.`,
    drivers: driverDetails
  };
};

// ============================================
// 3. WHAT FALLS FLAT WITHOUT IT (current mode only)
// ============================================
export const generateWhatsNotComingThrough = (profile, scores) => {
  if (profile.mode === 'seeking') return null;

  const gaps = scores.topGaps;
  if (gaps.length === 0) return null;

  const items = gaps.map(g => ({
    pillarId: g.pillarId,
    name: getPillarName(g.pillarId),
    importance: g.importance
  }));

  return {
    text: 'These are important to you, but the experience does not land the same way without them.',
    items
  };
};

// ============================================
// 5. WHAT THIS TELLS YOU
// ============================================
export const generateWhatThisTellsYou = (profile, scores) => {
  const { topGaps, topDrivers } = scores;
  const isSeeking = profile.mode === 'seeking';
  const intentLabels = profile.intent.map(getIntentLabel);
  const intentStr = intentLabels.join(' and ').toLowerCase();
  const driverNames = topDrivers.slice(0, 2).map(d => getPillarName(d.pillarId).toLowerCase());
  const driverStr = driverNames.join(' and ');

  if (isSeeking) {
    return `This gives you a clear map of what makes a ${intentStr} experience work for you. ${driverStr} ${driverNames.length === 1 ? 'is' : 'are'} what this experience depends on. When ${driverNames.length === 1 ? 'this element is' : 'these elements are'} present, this is likely to feel right. When ${driverNames.length === 1 ? 'it is' : 'they are'} not, it may feel flat — even if everything looks right on the surface.`;
  }

  const topGap = topGaps[0];
  const gapName = topGap ? getPillarName(topGap.pillarId).toLowerCase() : '';

  if (gapName) {
    return `You are looking for a ${intentStr} experience. ${driverStr} ${driverNames.length === 1 ? 'is' : 'are'} what makes this work. When ${driverStr} ${driverNames.length === 1 ? 'is' : 'are'} present, it tends to feel right. When ${gapName} is not there, the experience falls flat — even if everything else looks fine.`;
  }

  return `You are looking for a ${intentStr} experience. ${driverStr} ${driverNames.length === 1 ? 'is' : 'are'} what makes this work. When ${driverNames.length === 1 ? 'this is' : 'these are'} present, the experience tends to feel right. When ${driverNames.length === 1 ? 'it is' : 'they are'} not, it tends to fall flat — even if everything else looks fine on the surface.`;
};

// ============================================
// 6. REAL-LIFE TRANSLATION
// ============================================
export const generateRealLifeTranslation = (scores) => {
  return scores.topDrivers.slice(0, 2).map(driver => {
    const translation = PILLAR_TRANSLATIONS[driver.pillarId];
    if (!translation) return null;

    return {
      pillarId: driver.pillarId,
      name: translation.name,
      whenPresent: translation.whenPresent,
      whenMissing: translation.whenMissing,
      whatItPointsTo: translation.whatItPointsTo
    };
  }).filter(Boolean);
};

// ============================================
// 7. CONTEXT LENS
// ============================================
export const generateContextLens = (profile) => {
  if (profile.mode === 'current') {
    return 'This reflects your current lived experience. When something feels off, it points to a real condition — not a shortcoming.';
  }
  return 'This reflects a desired experience — what you are looking for, not what is wrong.';
};

// ============================================
// 8. PUT IT INTO WORDS
// ============================================
export const generatePutItIntoWords = (profile, scores) => {
  const intentLabels = profile.intent.map(getIntentLabel);
  const intentStr = intentLabels.join(' and ').toLowerCase();
  const topDriverNames = scores.topDrivers.slice(0, 2).map(d => getPillarName(d.pillarId).toLowerCase());
  const isSeeking = profile.mode === 'seeking';

  if (isSeeking) {
    const topDep = scores.topGaps[0];
    const depName = topDep ? getPillarName(topDep.pillarId).toLowerCase() : '';
    if (depName) {
      return `I know what makes a ${intentStr} experience work for me. It depends on ${topDriverNames.join(' and ')}. Without ${depName}, it does not really land — and now I understand why.`;
    }
    return `I know what makes a ${intentStr} experience work for me. ${topDriverNames.join(' and ')} ${topDriverNames.length === 1 ? 'is' : 'are'} what this depends on.`;
  }

  const topGap = scores.topGaps[0];
  const gapName = topGap ? getPillarName(topGap.pillarId).toLowerCase() : '';

  if (gapName) {
    return `I am looking for a ${intentStr} experience where ${topDriverNames.join(' and ')} ${topDriverNames.length === 1 ? 'is' : 'are'} central. Without ${gapName}, it does not really land for me.`;
  }

  return `I know what makes a ${intentStr} experience work for me. ${topDriverNames.join(' and ')} ${topDriverNames.length === 1 ? 'is' : 'are'} what this depends on. When ${topDriverNames.length === 1 ? 'this is' : 'these are'} present, it feels right.`;
};

// ============================================
// 9. HAEVN PROFILE SUMMARY
// ============================================
export const generateHAEVNSummary = (profile, scores) => {
  const topDriverNames = scores.topDrivers.slice(0, 2).map(d => getPillarName(d.pillarId));
  const topGap = scores.topGaps[0];
  const topGapName = topGap ? getPillarName(topGap.pillarId) : null;

  // Drive descriptions
  const driveDescriptions = {
    connection: 'You are driven by depth and emotional closeness. Without genuine connection, experiences tend to feel hollow.',
    desire: 'You are driven by being actively wanted. Without visible desire and pursuit, experiences lose their charge.',
    novelty: 'You are driven by freshness and discovery. Without new energy, even good experiences go flat.',
    power: 'You are driven by tension and contrast. Without dynamic polarity, experiences feel neutral.',
    play: 'You are driven by lightness and freedom. Without play and ease, experiences feel heavy.'
  };

  const gapDescriptions = {
    connection: 'Experiences that lack emotional depth tend not to work, even if everything else is technically present.',
    desire: 'Experiences where you feel appreciated but not actively wanted tend to fall flat.',
    novelty: 'Predictable or routine experiences tend to lose your interest quickly.',
    power: 'Experiences that feel too equal or negotiated tend to lack the spark you need.',
    play: 'Experiences that feel serious or performative tend to shut things down.'
  };

  const lookForDescriptions = {
    connection: 'Look for depth over intensity. The quality of presence matters more than anything.',
    desire: 'Look for experiences where desire is visible and active, not assumed.',
    novelty: 'Look for environments that allow exploration and change.',
    power: 'Look for dynamics where roles are clear and tension is welcome.',
    play: 'Look for experiences where you can be imperfect, playful, and unscripted.'
  };

  const topDriverId = scores.topDrivers[0]?.pillarId;
  const gapId = topGap?.pillarId;

  return {
    topDrivers: topDriverNames,
    whatDrivesYou: driveDescriptions[topDriverId] || (topDriverNames.length > 0 ? `Your strongest drivers are ${topDriverNames.join(' and ')}.` : 'Your drivers are relatively balanced across all dimensions. No single ingredient stands out above the rest.'),
    whatDoesntWork: gapId
      ? (gapDescriptions[gapId] || `Experiences lacking ${topGapName?.toLowerCase()} tend not to work.`)
      : 'Your needs appear relatively balanced. No single dimension stands out as critically absent.',
    whatToLookFor: gapId
      ? (lookForDescriptions[gapId] || `Pay attention to how ${topGapName?.toLowerCase()} shows up.`)
      : 'Continue paying attention to what makes experiences feel alive versus flat.',
    structureType: {
      connection: 'Connection-driven',
      desire: 'Desire-driven',
      novelty: 'Novelty-driven',
      power: 'Tension-driven',
      play: 'Play-driven'
    }[topDriverId] || 'Balanced',
    contextSensitivity: (() => {
      const importances = scores.pillarScores.map(s => s.importance);
      const range = Math.max(...importances) - Math.min(...importances);
      if (range >= 5) return 'Highly specific — certain ingredients matter much more than others';
      if (range >= 3) return 'Moderately specific — clear preferences but flexible';
      return 'Broadly oriented — most dimensions matter similarly';
    })(),
    readiness: profile.mode === 'seeking'
      ? 'Clearly defined — you know what this experience depends on'
      : 'Defined — you know what makes this work and what causes it to fall flat'
  };
};

// ============================================
// 10. REFLECTION PROMPTS
// ============================================
export const generateReflectionPrompts = (profile, scores) => {
  const isSeeking = profile.mode === 'seeking';
  const topGap = scores.topGaps[0];

  if (isSeeking) {
    const prompts = ['When has this kind of experience actually worked for you?'];
    if (topGap) {
      const depName = getPillarName(topGap.pillarId).toLowerCase();
      prompts.push(`What does it look like when ${depName} is present?`);
    } else {
      prompts.push('What conditions make this feel right?');
    }
    prompts.push('What would this experience need to feel like it was built for you?');
    return prompts;
  }

  const prompts = ['When has this actually worked for you?'];
  if (topGap) {
    const gapName = getPillarName(topGap.pillarId).toLowerCase();
    prompts.push(`What would it look like if ${gapName} were more present in this experience?`);
  } else {
    prompts.push('What conditions would need to change for this to feel right?');
  }
  prompts.push('What does this experience feel like when it is working?');
  return prompts.slice(0, 3);
};

// ============================================
// CROSS-PROFILE COMPARISON
// ============================================
export const generateCrossProfileAnalysis = (profiles) => {
  if (!profiles || profiles.length < 2) return null;

  const insights = [];
  const allTopDrivers = profiles.map(p => {
    const drivers = Object.entries(p.drivers || {})
      .sort(([, a], [, b]) => b.importance - a.importance)
      .slice(0, 2)
      .map(([id]) => id);
    return { label: p.label, drivers };
  });

  // Check for shared drivers
  const firstDrivers = new Set(allTopDrivers[0]?.drivers || []);
  const sharedAcross = allTopDrivers[0]?.drivers.filter(d =>
    allTopDrivers.every(p => p.drivers.includes(d))
  ) || [];

  if (sharedAcross.length > 0) {
    const names = sharedAcross.map(getPillarName);
    insights.push({
      type: 'shared',
      text: `${names.join(' and ')} ${names.length === 1 ? 'appears' : 'appear'} important across all your contexts. This is likely a core part of what works for you.`
    });
  }

  // Check cross-context differences
  const availabilities = profiles.map(p => ({ label: p.label, availability: p.availability }));
  const maxAvail = availabilities.reduce((a, b) => a.availability > b.availability ? a : b);
  const minAvail = availabilities.reduce((a, b) => a.availability < b.availability ? a : b);
  if (maxAvail.availability - minAvail.availability >= 4) {
    insights.push({
      type: 'fulfillment',
      text: `This experience feels more present in "${maxAvail.label}" than in "${minAvail.label}." Your needs may be similar, but the conditions differ.`
    });
  }

  // Different intents
  const intentSets = profiles.map(p => p.intent);
  const allIntentsMatch = intentSets.every(set =>
    JSON.stringify(set.sort()) === JSON.stringify(intentSets[0].sort())
  );
  if (!allIntentsMatch) {
    insights.push({
      type: 'intent',
      text: 'You are looking for different kinds of experience in different contexts. That is not inconsistency — it is range.'
    });
  }

  return {
    insights: insights.slice(0, 3),
    summary: insights.length > 0
      ? 'Your profiles show that context shapes experience. What works in one setting may not apply in another.'
      : 'Your profiles appear relatively consistent across contexts.'
  };
};

// ============================================
// COMBINED SUMMARY (for multi-profile)
// ============================================
export const generateCombinedSummary = (profiles) => {
  if (!profiles || profiles.length < 2) return null;

  const allDriverIds = {};
  profiles.forEach(p => {
    Object.entries(p.drivers || {}).forEach(([id, d]) => {
      if (!allDriverIds[id]) allDriverIds[id] = [];
      allDriverIds[id].push(d.importance);
    });
  });

  const avgDrivers = Object.entries(allDriverIds)
    .map(([id, vals]) => ({
      id,
      avg: vals.reduce((s, v) => s + v, 0) / vals.length
    }))
    .sort((a, b) => b.avg - a.avg);

  const topOverall = avgDrivers.slice(0, 2).map(d => getPillarName(d.id).toLowerCase());
  const labels = profiles.map(p => `"${p.label}"`).join(', ');

  let narrative = `Across ${labels}, a pattern emerges. `;
  narrative += `${topOverall.join(' and ')} ${topOverall.length === 1 ? 'appears' : 'appear'} to be central to what works for you, regardless of context. `;

  const avgAvail = profiles.reduce((s, p) => s + (p.availability || 5), 0) / profiles.length;
  if (avgAvail >= 7) {
    narrative += 'Overall, these experiences feel present and working.';
  } else if (avgAvail >= 4) {
    narrative += 'How fully this is present varies — some contexts serve you better than others.';
  } else {
    narrative += 'What you want is clear. The conditions are not yet in place across these contexts.';
  }

  return { narrative };
};

// ============================================
// MASTER RUN
// ============================================
export const runFullAnalysis = (profile, scores) => {
  return {
    intentAnchor: generateIntentAnchor(profile, scores),
    whatHitsHardest: generateWhatHitsHardest(profile, scores),
    whatsNotComingThrough: generateWhatsNotComingThrough(profile, scores),
    whatThisTellsYou: generateWhatThisTellsYou(profile, scores),
    experienceConfidenceLabel: profile.mode === 'seeking'
      ? getConfidenceLabel(profile.experienceConfidence)
      : null,
    experienceConfidenceShort: profile.mode === 'seeking'
      ? getConfidenceShort(profile.experienceConfidence)
      : null,
    realLifeTranslation: generateRealLifeTranslation(scores),
    contextLens: generateContextLens(profile),
    putItIntoWords: generatePutItIntoWords(profile, scores),
    haevnSummary: generateHAEVNSummary(profile, scores),
    reflectionPrompts: generateReflectionPrompts(profile, scores)
  };
};
